import { Controller, useFormContext } from "react-hook-form"
import { DualColumnRow, Subtitle } from "../../../../../../style"
import {
  TextField,
  parsePhoneNumberField,
} from "../../../../../../../../../../components/TextField"
import { Trans, t } from "@lingui/macro"

import { CheckboxField } from "../../../../../../../../../../components/CheckboxField"
import { DropdownField } from "../../../../../../../../../../components/DropdownField"
import { IdentityNumberController } from "../../../../../IdentityNumberController"
import { IdentityType } from "../../../../../../../../../../api/stores/checkout"
import { ReceiverData } from "../../../../types"
import { i18n } from "@lingui/core"
import { useCopyReceiver } from "../../hooks/useCopyReceiver"

type Keys = keyof Omit<ReceiverData, "title">

type Fields = {
  [key in Keys]?: boolean
}

export type ReceiverFormProps = {
  title: JSX.Element | string
  fields?: Fields
}

type Option = { id: string; name: string; value: IdentityType }

const arrayOfAllOptions: ArrayOfAllUnion<Option> = array => array

export const isFieldValid = (field?: boolean) =>
  field === true || field === undefined

export const ReceiverForm = ({
  title,
  fields = {
    isReceiverSameAsContact: true,
    receiverFirstName: true,
    receiverLastName: true,
    receiverIdentityType: true,
    receiverIdentityNumber: true,
    receiverPhone: true,
  },
}: ReceiverFormProps) => {
  const { control, errors, getValues, watch } = useFormContext<ReceiverData>()
  const disableFields = useCopyReceiver()

  return (
    <>
      <Subtitle>{title}</Subtitle>

      {isFieldValid(fields.isReceiverSameAsContact) && (
        <Controller
          control={control}
          name="isReceiverSameAsContact"
          render={({ onChange, value }) => (
            <CheckboxField
              label={<Trans>Usar los mismos datos de compra</Trans>}
              value={value ? "" : "true"}
              checked={value === "true"}
              onChange={onChange}
            />
          )}
        />
      )}

      <DualColumnRow>
        {isFieldValid(fields.receiverFirstName) && (
          <Controller
            control={control}
            name="receiverFirstName"
            defaultValue=""
            rules={{
              required: t`Completá el campo con tu nombre.`,
            }}
            render={({ onChange, value, name }) => (
              <TextField
                label={<Trans>Nombre</Trans>}
                errorMsg={errors.receiverFirstName?.message}
                disabled={disableFields}
                maxLength={100}
                hideLength={true}
                name={name}
                onChange={onChange}
                value={value}
              />
            )}
          />
        )}

        {isFieldValid(fields.receiverLastName) && (
          <Controller
            control={control}
            name="receiverLastName"
            defaultValue=""
            rules={{
              required: t`Completá el campo con tu apellido.`,
            }}
            render={({ onChange, value, name }) => (
              <TextField
                label={<Trans>Apellido</Trans>}
                errorMsg={errors.receiverLastName?.message}
                disabled={disableFields}
                maxLength={100}
                hideLength={true}
                name={name}
                onChange={onChange}
                value={value}
              />
            )}
          />
        )}

        {isFieldValid(fields.receiverIdentityType) && (
          <Controller
            control={control}
            name="receiverIdentityType"
            defaultValue={i18n.locale === "es-MX" ? "OTHER" : "DU"}
            rules={{
              required: t`Elegí el tipo de documento.`,
            }}
            render={({ onChange, value, name }) => (
              <DropdownField
                name={name}
                label={<Trans>Tipo de documento</Trans>}
                errorMsg={errors.receiverIdentityType?.message}
                disabled={disableFields}
                options={arrayOfAllOptions(
                  i18n.locale === "es-MX"
                    ? [
                        { id: "3", name: t`Otro`, value: "OTHER" },
                        { id: "1", name: t`DNI`, value: "DU" },
                        { id: "2", name: t`CUIT`, value: "CUIT" },
                      ]
                    : [
                        { id: "1", name: t`DNI`, value: "DU" },
                        { id: "2", name: t`CUIT`, value: "CUIT" },
                        { id: "3", name: t`Otro`, value: "OTHER" },
                      ]
                )}
                onChange={onChange}
                value={value}
              />
            )}
          />
        )}

        {isFieldValid(fields.receiverIdentityNumber) && (
          <IdentityNumberController
            name="receiverIdentityNumber"
            errorMsg={errors.receiverIdentityNumber?.message}
            control={control}
            selectedIdentityType={watch("receiverIdentityType") as IdentityType}
            disabled={disableFields}
            defaultValue={getValues("receiverIdentityNumber")}
          />
        )}
      </DualColumnRow>

      {isFieldValid(fields.receiverPhone) && (
        <Controller
          control={control}
          name="receiverPhone"
          defaultValue=""
          rules={{
            required: t`Completá el campo con tu teléfono.`,
          }}
          render={({ onChange, value, name }) => (
            <TextField
              name={name}
              label={<Trans>Teléfono</Trans>}
              errorMsg={errors.receiverPhone?.message}
              disabled={disableFields}
              type="tel"
              maxLength={20}
              hideLength={true}
              onChange={e => onChange(parsePhoneNumberField(e.target.value))}
              value={value}
            />
          )}
        />
      )}
    </>
  )
}
